import { ref } from 'vue'
import type { Ref } from 'vue'
import type { Invitation, SendInvitationPayload } from '@/types/team'
import {
  listInvitations as apiListInvitations,
  createInvitation as apiCreateInvitation,
  cancelInvitation as apiCancelInvitation,
  resendInvitation as apiResendInvitation,
} from '@/api/invitations'

const invitations = ref<Invitation[]>([])
const loading = ref(false)
const sending = ref(false)
const saving = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref<string | null>(null)
// Raw message supplied by the backend (e.g. a 422 rejection of a resend), shown as-is.
const actionErrorMessage = ref<string | null>(null)

function isValidationError(
  e: unknown,
): e is { response: { data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { data?: unknown } }).response !== null &&
    typeof (e as { response: { data: unknown } }).response.data === 'object'
  )
}

function backendMessage(e: unknown): string | null {
  if (typeof e !== 'object' || e === null || !('response' in e)) return null
  const response = (e as { response: { status?: unknown; data?: { message?: unknown } | null } | null })
    .response
  if (!response || response.status !== 422) return null
  const message = response.data?.message
  return typeof message === 'string' && message !== '' ? message : null
}

async function fetchInvitations(organisationId: number): Promise<void> {
  loading.value = true
  generalError.value = null
  try {
    invitations.value = await apiListInvitations(organisationId)
  } catch {
    generalError.value = 'team.errors.load_invitations_failed'
  } finally {
    loading.value = false
  }
}

async function createInvitation(
  organisationId: number,
  payload: SendInvitationPayload,
): Promise<Invitation | null> {
  sending.value = true
  errors.value = {}
  generalError.value = null
  try {
    const created = await apiCreateInvitation(organisationId, payload)
    invitations.value = [...invitations.value, created]
    return created
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = 'team.errors.send_invitation_failed'
    }
    return null
  } finally {
    sending.value = false
  }
}

async function cancelInvitation(organisationId: number, invitationId: number): Promise<boolean> {
  if (saving.value) return false
  saving.value = true
  generalError.value = null
  actionErrorMessage.value = null
  try {
    await apiCancelInvitation(organisationId, invitationId)
    invitations.value = invitations.value.map((inv) =>
      inv.id === invitationId ? { ...inv, status: 'cancelled' as const } : inv,
    )
    return true
  } catch (e: unknown) {
    const message = backendMessage(e)
    if (message !== null) {
      actionErrorMessage.value = message
    } else {
      generalError.value = 'team.errors.cancel_invitation_failed'
    }
    return false
  } finally {
    saving.value = false
  }
}

async function resendInvitation(
  organisationId: number,
  invitationId: number,
): Promise<Invitation | null> {
  if (saving.value) return null
  saving.value = true
  generalError.value = null
  actionErrorMessage.value = null
  try {
    const updated = await apiResendInvitation(organisationId, invitationId)
    invitations.value = invitations.value.map((inv) =>
      inv.id === invitationId ? updated : inv,
    )
    return updated
  } catch (e: unknown) {
    const message = backendMessage(e)
    if (message !== null) {
      actionErrorMessage.value = message
    } else {
      generalError.value = 'team.errors.resend_invitation_failed'
    }
    return null
  } finally {
    saving.value = false
  }
}

export function useTeamInvitations(): {
  invitations: Ref<Invitation[]>
  loading: Ref<boolean>
  sending: Ref<boolean>
  saving: Ref<boolean>
  errors: Ref<Record<string, string[]>>
  generalError: Ref<string | null>
  actionErrorMessage: Ref<string | null>
  fetchInvitations: (organisationId: number) => Promise<void>
  createInvitation: (
    organisationId: number,
    payload: SendInvitationPayload,
  ) => Promise<Invitation | null>
  cancelInvitation: (organisationId: number, invitationId: number) => Promise<boolean>
  resendInvitation: (
    organisationId: number,
    invitationId: number,
  ) => Promise<Invitation | null>
} {
  return {
    invitations,
    loading,
    sending,
    saving,
    errors,
    generalError,
    actionErrorMessage,
    fetchInvitations,
    createInvitation,
    cancelInvitation,
    resendInvitation,
  }
}
